import { isNull, isUndefined } from '@dimjs/lang';
import { models } from '../models/index.js';
import { logInfo } from '../utils/log.js';
import { getFullUrlHref } from '../utils/path.js';

const { useStore, getActions } = models;

/**
 * 注意此处的loading务必需要成对的关闭,展示, 通常在actions里面的每一个action方式配置一个唯一的loading
 * 执行成对的开/关,在具体得业务模块直接使用`useLoading`来获取当前模块状态即可.
 * @param id 字符串类型, 标识每次独立业务的loading唯一id
 * @param urlHref 当前URL, 如果快速点击的情况可以自己传递URL来解决来指定获取的loading context
 * @returns `true` 标识显示loading, `false` 不显示.
 */
export const useLoading = <T extends string | ''>(id: T, urlHref?: string) => {
  const crrUrlHref = urlHref || getFullUrlHref();

  const [isLoading] = useStore('FabricFabricLoading', (state) => {
    logInfo(state.loading);
    const loadingCtx = state.loading[crrUrlHref] || {};
    const loadingCounter = loadingCtx[id];
    if (isNull(loadingCounter) || isUndefined(loadingCounter)) {
      return false;
    }
    return loadingCounter !== 0;
  });
  return isLoading;
};

/**
 * 我们可以通过此函数在 业务`models`的`actions`去 show一个loading状态
 * ```ts
 * export type LoadingModelXTypes = 'global' | 'xxxuid';
 * show<LoadingModelXTypes>('global')
 * ```
 * @param id 当前业务唯一标识, 在hide的时候需要传入此参数
 * @param urlHref 当前URL, 如果快速点击的情况可以自己传递URL来解决.
 *
 */
export const show = async <T extends string>(
  id: T,
  urlHref?: string
): Promise<void> => {
  await getActions('FabricFabricLoading').show({
    id,
    urlHref: urlHref || getFullUrlHref(),
  });
};

/**
 * 我们可以通过此函数在 业务`models`的`actions`去show一个loading状态
 * @example
 * ```ts
 * export type LoadingModelXTypes = 'global' | 'xxxuid';
 * hide<LoadingModelXTypes>('global')
 * ```
 * @param id 当前业务唯一标识, 在hide的时候需要传入此参数
 * @param closeAll 是否强制关闭所有的loading
 * @param urlHref 当前URL, 如果快速点击的情况可以自己传递URL来解决.
 */
export const hide = async <T extends string>(
  id: T,
  closeAll = false,
  urlHref?: string
): Promise<void> => {
  await getActions('FabricFabricLoading').hide({
    id,
    urlHref: urlHref || getFullUrlHref(),
    closeAll,
  });
};

/**
 * 全局layout内容区域的spinner
 */
export const GLOBAL_SPINNING = 'GLOBAL_SPINNINGNING';

/**
 * @internal
 */
export const showSpinningGlobal = async (urlHref: string): Promise<void> => {
  await getActions('FabricFabricLoading').show({
    id: GLOBAL_SPINNING,
    urlHref,
  });
};

/**
 * @internal
 */
export const hideSpinningGlobal = async (urlHref: string): Promise<void> => {
  await getActions('FabricFabricLoading').hide({
    urlHref,
    id: GLOBAL_SPINNING,
    closeAll: false,
  });
};

/**
 * 全局的加载效果
 * @returns
 */
export const useLoadingGlobal = () => {
  return useLoading(GLOBAL_SPINNING);
};
